import { HeaderText } from '@/components/header-text';
import { PlumeBadge } from '@/components/plume-badge';
import { RoundedBadge } from '@/components/rounded-badge';
import { DescriptionText } from '@/components/text/description';
import { TooltipText } from '@/components/tooltip-text';
import { useGuardiansCampaignStats } from '@/hooks/useGuardiansCampaignStats';
import { useGuardiansCampaignUserStats } from '@/hooks/useGuardiansCampaignUserStats';
import { Campaign, CampaignTier, CampaignTierId, GuardiansCampaignUserStats } from '@/types';
import { getIconPathFromBadgeId } from '@/utils';
import {
  Box,
  Card,
  FormatNumber,
  HStack,
  Icon,
  Image,
  Skeleton,
  Table,
  TableCellProps,
  TableHeader,
  Text,
  useBreakpointValue,
  VStack,
} from '@chakra-ui/react';
import { ReactElement, ReactNode } from 'react';
import { FaCheck } from 'react-icons/fa6';
import { tierIdToPresentationalData } from './constants';

export const ContributionTiers = () => {
  const showTable = useBreakpointValue({
    base: false,
    sm: false,
    md: true,
  });
  const { guardiansCampaignStats } = useGuardiansCampaignStats();

  const { guardiansCampaignUserStats } = useGuardiansCampaignUserStats();

  const { campaign } = guardiansCampaignStats ?? {};
  return (
    <Box w="full">
      <HeaderText mb={3}>Contribution tiers</HeaderText>
      {showTable ? (
        <ContributionTiersTable campaign={campaign} userStats={guardiansCampaignUserStats} />
      ) : (
        <ContributionTiersList campaign={campaign} userStats={guardiansCampaignUserStats} />
      )}
    </Box>
  );
};

const ContributionTiersList = ({
  campaign,
  userStats,
}: {
  campaign?: Campaign;
  userStats?: GuardiansCampaignUserStats;
}) => (
  <VStack w="full">
    {campaign?.tiers.map((tier) => {
      const presentationalData = tierIdToPresentationalData[tier.id as keyof typeof tierIdToPresentationalData];
      if (!presentationalData) return null;
      const unlocked = contributionTierUnlocked(tier, userStats, campaign?.endDate);
      return (
        <Card.Root rounded="2xl" w="full" p={4} key={tier.id} h={256}>
          <Card.Body p={2}>
            <VStack w="full" justifyContent="space-between" h="full">
              <HStack w="full">
                {tier.badgeId && <PlumeBadge badgeId={tier.badgeId} earned={unlocked} />}
                <Box>
                  <DescriptionText>{presentationalData.tierLevel}</DescriptionText>
                  <HeaderText fontWeight="500" fontSize="lg">
                    {presentationalData.description}
                  </HeaderText>
                </Box>
              </HStack>
              <ContributionTierRow
                title="Status"
                value={
                  <ContributionTierStatus
                    unlocked={unlocked}
                    position={userStats?.rank}
                    tierId={tier.id}
                    campaignEndDate={campaign.endDate}
                  />
                }
              />
              <ContributionTierRow
                title="Requirement"
                value={tier.tx ? `${tier.tx}+ Transactions` : 'Top 100 leaderboard when the campaign ends'}
              />
              <ContributionTierRow
                title="Rewards"
                value={<ContributionTierRewards pp={tier.pp} badgeId={tier.badgeId} unlocked={unlocked} />}
              />
            </VStack>
          </Card.Body>
        </Card.Root>
      );
    })}
  </VStack>
);

const ContributionTierRow = ({ title, value }: { title: string; value: ReactElement | string }) => {
  return (
    <HStack w="full" justifyContent="space-between">
      <DescriptionText>{title}</DescriptionText>
      {value}
    </HStack>
  );
};

const ContributionTierStatus = ({
  unlocked,
  position = 0,
  tierId,
  campaignEndDate,
}: {
  unlocked: boolean;
  position?: number;
  tierId: CampaignTierId;
  campaignEndDate?: string;
}) => {
  if (!campaignEndDate) return <Skeleton />;
  const endDate = new Date(campaignEndDate);
  const now = new Date();
  if (tierId === 'bonusTier' && now < endDate) {
    if (!position || position > 100) {
      return (
        <DescriptionText color={unlocked ? 'fg' : 'fg.subtle'}>
          Your position: {position ? <FormatNumber value={position} /> : 'N/A'}
        </DescriptionText>
      );
    }
    return (
      <RoundedBadge bgColor="#FFA5831A" h={8}>
        🔥<DescriptionText color="#963D01">On Track</DescriptionText>
      </RoundedBadge>
    );
  }
  if (unlocked) {
    return (
      <RoundedBadge bgColor="#EAF6F3" h={8}>
        <Icon color="#00D594">
          <FaCheck />
        </Icon>{' '}
        <DescriptionText color="#004038">Completed</DescriptionText>
      </RoundedBadge>
    );
  }
  return (
    <RoundedBadge h={8} color="fg.subtle">
      🔒 Locked
    </RoundedBadge>
  );
};

const StyledTableCell = ({ children, ...rest }: { children: ReactNode } & TableCellProps) => (
  <Table.Cell borderColor="bg.subtle" py={6} border="1px solid #f0f0f0" borderRight="none" borderLeft="none" {...rest}>
    {children}
  </Table.Cell>
);
const ContributionTiersTable = ({
  campaign,
  userStats,
}: {
  campaign?: Campaign;
  userStats?: GuardiansCampaignUserStats;
}) => (
  <Table.Root
    borderCollapse="separate"
    borderSpacing={'0 10px'}
    fontWeight="500"
    fontFamily="Inter, sans-serif"
    fontSize="md"
  >
    <TableHeader>
      <Table.Row fontSize="sm" bg="transparent">
        <Table.ColumnHeader borderBottom={'none'} color="fg.muted">
          Tier
        </Table.ColumnHeader>
        <Table.ColumnHeader borderBottom={'none'} color="fg.muted">
          Status
        </Table.ColumnHeader>
        <Table.ColumnHeader borderBottom={'none'} color="fg.muted">
          Requirement
        </Table.ColumnHeader>
        <Table.ColumnHeader borderBottom={'none'} color="fg.muted">
          <HStack justifyContent="end">
            <TooltipText
              text="Rewards"
              content="Points are updated every midnight UTC. Earned badges are awarded every few hours."
            />
          </HStack>
        </Table.ColumnHeader>
      </Table.Row>
    </TableHeader>
    <Table.Body>
      {campaign?.tiers.map((tier) => {
        const presentationalData = tierIdToPresentationalData[tier.id as keyof typeof tierIdToPresentationalData];
        if (!presentationalData) return null;
        const unlocked = contributionTierUnlocked(tier, userStats, campaign?.endDate);
        return (
          <Table.Row mb={2} color={unlocked ? 'initial' : 'fg.subtle'} key={tier.id}>
            <StyledTableCell borderTopLeftRadius="xl" borderBottomLeftRadius="xl" borderLeft="1px solid muted" pl={6}>
              <HStack gap={4}>
                <PlumeBadge badgeId={tier.badgeId} earned={unlocked} />
                <VStack alignItems="flex-start">
                  <Text fontSize="md" fontWeight="500" color={unlocked ? 'fg.muted' : 'fg.subtle'}>
                    {presentationalData.tierLevel}
                  </Text>
                  <Text fontSize="lg" fontFamily="heading" fontWeight="500">
                    {presentationalData.description}
                  </Text>
                </VStack>
              </HStack>
            </StyledTableCell>
            <StyledTableCell>
              {/* if tier not in earned tiers, show locked.
                        calculate earned tiers in top of component
                         */}
              <ContributionTierStatus
                unlocked={unlocked}
                position={userStats?.rank}
                tierId={tier.id}
                campaignEndDate={campaign.endDate}
              />
            </StyledTableCell>
            <StyledTableCell>
              {tier.tx ? tier.tx + '+ Transactions' : 'Top 100 leaderboard when the campaign ends'}
            </StyledTableCell>
            <StyledTableCell
              borderTopRightRadius="xl"
              borderBottomRightRadius="xl"
              borderRight="1px solid muted"
              pr={6}
              minW={230}
            >
              <ContributionTierRewards pp={tier.pp} badgeId={tier.badgeId} unlocked={unlocked} />
            </StyledTableCell>
          </Table.Row>
        );
      })}
    </Table.Body>
  </Table.Root>
);

const contributionTierUnlocked = (
  tier: CampaignTier,
  userStats?: GuardiansCampaignUserStats,
  campaignEndDate?: string,
) => {
  if (tier.id !== 'bonusTier') return Boolean(tier.tx && userStats?.txCount && tier.tx <= userStats.txCount);
  if (!campaignEndDate) return false;

  const endDate = new Date(campaignEndDate);
  const now = new Date();
  if (!userStats?.rank) return false;
  return now > endDate && userStats.rank <= 100;
};

const ContributionTierRewards = ({
  pp,
  badgeId,
  unlocked,
}: {
  pp: number | null | undefined;
  badgeId: string;
  unlocked: boolean;
}) => (
  <HStack>
    <RoundedBadge h={8} color={unlocked ? 'fg' : 'fg.subtle'}>
      <Image src="/assets/pp-square.svg" /> {pp} PP
    </RoundedBadge>
    <RoundedBadge h={8} color={unlocked ? 'fg' : 'fg.subtle'}>
      {<Image boxSize={6} src={getIconPathFromBadgeId(badgeId, true)} />}
      Badge
    </RoundedBadge>
  </HStack>
);
